import { isAdminMarketingUser } from "@redotech/admin-sdk/marketing/constants";
import { ORDER_TRACKING_BILLING_DEFAULTS } from "@redotech/redo-billing-common/billing-defaults";
import { PaymentProviderType } from "@redotech/redo-billing-common/billing-engine-types";
import {
  BillingPeriod,
  billingPeriodToFriendlyUpper,
} from "@redotech/redo-billing-common/service-invoice";
import { teamGetPaymentMethod } from "@redotech/redo-model/team";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { Card } from "@redotech/redo-web/card";
import { DateInput } from "@redotech/redo-web/date-picker";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { FormRadioGroup } from "@redotech/redo-web/radio";
import { FormSwitch } from "@redotech/redo-web/switch";
import { Text } from "@redotech/redo-web/text";
import { FormTextInput } from "@redotech/redo-web/text-input";
import { InputSchema, groupInput, input } from "@redotech/ui/form";
import {
  areDatesEqual,
  getDateStartOfDayInTimezone,
} from "@redotech/util/date";
import { memo, useContext, useEffect, useMemo } from "react";
import { AuthContext } from "../../../app/auth";
import { TeamContext } from "../../team";

export const orderTrackingBillingForm = groupInput({
  enabled: input<boolean>(),
  invoiceBilling: groupInput(
    {
      enabled: input<boolean>(),
      paymentProvider: input<PaymentProviderType>(),
      renewOnPeriodEnd: input<boolean>(),
      startDate: input<Date | null>({ equal: areDatesEqual }),
      period: input<BillingPeriod>(),
      periodFeeUsd: input<number>(),
      perOrderSettings: groupInput({
        enabled: input<boolean>(),
        includedOrders: input<number>(),
        overagePricePerOrderUsd: input<number>(),
      }),
      perShipmentSettings: groupInput({
        enabled: input<boolean>(),
        includedShipments: input<number>(),
        overagePricePerShipmentUsd: input<number>(),
      }),
      perSmsSettings: groupInput({
        enabled: input<boolean>(),
        includedCredits: input<number>(),
        overagePricePerSmsUsd: input<number>(),
        overagePricePerMmsUsd: input<number>(),
        chargeCarrierFees: input<boolean>(),
      }),
      perEmailSettings: groupInput({
        enabled: input<boolean>(),
        includedEmails: input<number>(),
        overagePricePerEmailUsd: input<number>(),
      }),
      /**
       * This has to be a separate field because evaluating it requires
       * knowing the team's timezone, which we don't have in the form validator.
       */
      startDateIsUpdatedToThePast: input<boolean>(),
    },
    {
      validator: (value) => {
        if (value.enabled && !value.startDate) {
          return ["Start date is required when invoice billing is enabled"];
        }
        if (value.startDateIsUpdatedToThePast) {
          return ["Start date cannot be in the past"];
        }
        return [];
      },
    },
  ),
  continueIfNotAccepted: input<boolean>(),
  pricePerOrder: input<string>(),
  freeOrdersPerMonth: input<string>(),
  freeUntilDateEnabled: input<boolean>(),
  rerouteBillingToMarketing: input<boolean>(),
  freeUntilDate: input<string>(),
  texts: groupInput({
    pricePerSms: input<string>(),
    pricePerMms: input<string>(),
  }),
});

export type OrderTrackingBillingForm = InputSchema.Input<
  typeof orderTrackingBillingForm
>;

export type OrderTrackingBillingValue = InputSchema.Value<
  typeof orderTrackingBillingForm
>;

export const orderTrackingBillingDefault: OrderTrackingBillingValue = {
  enabled: false,
  invoiceBilling: {
    enabled: false,
    paymentProvider: PaymentProviderType.SHOPIFY,
    renewOnPeriodEnd: true,
    startDate: null,
    period: BillingPeriod.MONTHLY,
    periodFeeUsd: ORDER_TRACKING_BILLING_DEFAULTS.PERIOD_FEE_USD,
    perOrderSettings: {
      enabled: false,
      includedOrders: ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_ORDERS,
      overagePricePerOrderUsd:
        ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_ORDER_USD,
    },
    perShipmentSettings: {
      enabled: false,
      includedShipments: ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_SHIPMENTS,
      overagePricePerShipmentUsd:
        ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_SHIPMENT_USD,
    },
    perSmsSettings: {
      enabled: false,
      includedCredits: ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_CREDITS,
      overagePricePerSmsUsd:
        ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_SMS_USD,
      overagePricePerMmsUsd:
        ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_MMS_USD,
      chargeCarrierFees: false,
    },
    perEmailSettings: {
      enabled: false,
      includedEmails: ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_EMAILS,
      overagePricePerEmailUsd:
        ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_EMAIL_USD,
    },
    startDateIsUpdatedToThePast: false,
  },
  continueIfNotAccepted: false,
  pricePerOrder: (0.06).toString(),
  freeOrdersPerMonth: (100).toString(),
  freeUntilDateEnabled: false,
  rerouteBillingToMarketing: false,
  freeUntilDate: "",
  texts: { pricePerSms: (0.01).toString(), pricePerMms: (0.03).toString() },
};

export const OrderTrackingBillingCard = memo(function OrderTrackingBillingCard({
  input,
  smsEnabled,
  v2Enabled,
}: {
  input: OrderTrackingBillingForm;
  smsEnabled: boolean;
  v2Enabled: boolean;
}) {
  const {
    enabled,
    pricePerOrder,
    freeOrdersPerMonth,
    freeUntilDateEnabled,
    freeUntilDate,
    texts,
    invoiceBilling,
    continueIfNotAccepted,
    rerouteBillingToMarketing,
  } = input.inputs;
  const auth = useContext(AuthContext);
  const isAuthorized = isAdminMarketingUser(auth?.email);
  const team = useContext(TeamContext);
  const activeSubscription = team?.settings?.shopifySubscription?.[0];
  const isEnabled = team?.settings.orderTracking?.billing?.enabled;
  const teamTimezone = team?._shopify?.iana_timezone || "America/Los_Angeles";
  const now = getDateStartOfDayInTimezone(new Date(), teamTimezone);

  const subscriptionAlreadyStarted = useMemo(() => {
    const { enabled, startDate } = invoiceBilling.inputs;
    const alreadyEnabled = !enabled.changed && enabled.value;

    if (!alreadyEnabled) {
      return false;
    }

    const startDateIsTodayOrBefore =
      !!startDate.value &&
      getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <=
        now.getTime();

    if (startDateIsTodayOrBefore) {
      return true;
    }

    return false;
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamTimezone]);

  const shouldDisableToggle = useMemo(() => {
    const enabled = invoiceBilling.inputs.enabled;
    const startDate = invoiceBilling.inputs.startDate;
    const currentlyEnabled = enabled.value;
    const startDateIsInPast =
      !!startDate.value &&
      getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <
        now.getTime();

    return currentlyEnabled && startDateIsInPast;
  }, [
    invoiceBilling.inputs.enabled,
    invoiceBilling.inputs.startDate,
    now,
    teamTimezone,
  ]);

  const legacySettingsAreActive = useMemo(() => {
    const startDate = invoiceBilling.inputs.startDate;
    // Legacy settings are active if start date is null OR in the future
    if (!startDate.value) {
      return true;
    }
    return (
      getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() >
      now.getTime()
    );
  }, [invoiceBilling.inputs.startDate, now, teamTimezone]);

  useEffect(() => {
    const startDate = invoiceBilling.inputs.startDate;
    const startDateIsUpdatedToThePast =
      invoiceBilling.inputs.startDateIsUpdatedToThePast;

    if (!startDate.changed || !startDate.value) {
      if (startDateIsUpdatedToThePast.value) {
        startDateIsUpdatedToThePast.setValue(false);
      }
      return;
    }

    const startDateIsInThePast =
      getDateStartOfDayInTimezone(startDate.value, teamTimezone).getTime() <
      getDateStartOfDayInTimezone(now, teamTimezone).getTime();

    if (startDateIsUpdatedToThePast.value !== startDateIsInThePast) {
      startDateIsUpdatedToThePast.setValue(startDateIsInThePast);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceBilling, teamTimezone]);

  const legacyBillingSettings = (
    <>
      <FormSwitch
        disabled={!isAuthorized}
        input={freeUntilDateEnabled}
        label="Free Until Date"
      />
      <FormTextInput
        disabled={!isAuthorized || !freeUntilDateEnabled.value}
        input={freeUntilDate}
        label="Date"
        type="date"
      />
      <Text as="div" fontSize="sm" textColor="tertiary">
        Orders will be free until the specified date, after which billing will
        apply
      </Text>
      <FormSwitch
        disabled={!isAuthorized}
        input={continueIfNotAccepted}
        label="Continue if billing not accepted"
      >
        Enable the tracking page and send emails even if billing is not enabled
        or accepted
      </FormSwitch>
      <FormTextInput
        disabled={!isAuthorized}
        input={pricePerOrder}
        label="Price per order (USD)"
        max={1}
        min={0}
        prefix="$"
        step={0.001}
        type="number"
      />
      <FormTextInput
        disabled={!isAuthorized}
        input={freeOrdersPerMonth}
        label="Free orders per month"
        min={0}
        step={1}
        type="number"
      />

      {/* {texts.inputs.enabled.value && ( */}
      {smsEnabled && (
        <>
          <FormTextInput
            disabled={!isAuthorized}
            input={texts.inputs.pricePerSms}
            label="Price per SMS (USD)"
            max={1}
            min={0}
            prefix="$"
            step={0.001}
            type="number"
          />
          <FormTextInput
            disabled={!isAuthorized}
            input={texts.inputs.pricePerMms}
            label="Price per MMS"
            max={1}
            min={0}
            prefix="$"
            step={0.001}
            type="number"
          />
        </>
      )}
    </>
  );

  const invoiceBillingSettings = (
    <>
      <FormSwitch
        disabled={!isAuthorized}
        input={invoiceBilling.inputs.renewOnPeriodEnd}
        label="Renew when billing period ends"
      >
        Automatically renew subscription at the end of each billing period
      </FormSwitch>
      <LabeledInput
        description={
          <>
            We will charge the merchant their first period fee on this date.
            Until then, we will bill them according to the legacy (non-invoice)
            billing settings. If that's undesirable, set their legacy billing
            "Free Until Date" to be after this date.
          </>
        }
        label="Start date"
      >
        <DateInput
          disabled={!isAuthorized || subscriptionAlreadyStarted}
          value={
            invoiceBilling.inputs.startDate.value
              ? (() => {
                  // Convert stored date (midnight in merchant timezone) back to a local date for display
                  const storedDate = invoiceBilling.inputs.startDate.value;
                  const zonedDateTime = Temporal.Instant.fromEpochMilliseconds(
                    storedDate.getTime(),
                  ).toZonedDateTimeISO(teamTimezone);
                  // Create a date in local timezone that represents the same calendar day
                  return new Date(
                    zonedDateTime.year,
                    zonedDateTime.month - 1,
                    zonedDateTime.day,
                  );
                })()
              : null
          }
          valueChange={(date) => {
            if (!date) {
              invoiceBilling.inputs.startDate.setValue(date);
            } else {
              invoiceBilling.inputs.startDate.setValue(
                getDateStartOfDayInTimezone(date, teamTimezone),
              );
            }
          }}
        />
      </LabeledInput>
      <FormRadioGroup
        disabled={!isAuthorized}
        input={invoiceBilling.inputs.period}
        label="Billing period"
        optionLabel={(option) => {
          return billingPeriodToFriendlyUpper[option];
        }}
        options={Object.values(BillingPeriod)}
      >
        How often to bill for the monthly credit allowance
      </FormRadioGroup>
      <FormRadioGroup
        disabled={!isAuthorized}
        input={invoiceBilling.inputs.paymentProvider}
        label="Payment provider"
        optionLabel={(option) => {
          switch (option) {
            case PaymentProviderType.SHOPIFY:
              return "Shopify";
            case PaymentProviderType.STRIPE:
              return "Stripe";
            default:
              return option;
          }
        }}
        options={Object.values(PaymentProviderType)}
      >
        Select which payment provider to use for billing
      </FormRadioGroup>
      {invoiceBilling.inputs.paymentProvider.value ===
        PaymentProviderType.STRIPE &&
        !teamGetPaymentMethod(team?.stripe ?? undefined) && (
          <Text fontSize="xs" textColor="warning">
            Team has no stripe payment method configured. Billing will fail
            until the team adds a payment method.
          </Text>
        )}
      <RedoIncrementDecrement
        disabled={!isAuthorized}
        fullWidth
        label="Billing period fee (USD)"
        min={0}
        prefix="$"
        setValue={invoiceBilling.inputs.periodFeeUsd.setValue}
        step={0.000_001}
        value={invoiceBilling.inputs.periodFeeUsd.value}
      />
      <FormSwitch
        disabled={!isAuthorized}
        input={invoiceBilling.inputs.perOrderSettings.inputs.enabled}
        label="Bill Per Order"
      >
        Enable order-based billing
      </FormSwitch>
      {invoiceBilling.inputs.perOrderSettings.inputs.enabled.value && (
        <Flex dir="column" gap="xl" pl="xl">
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Included orders per month"
            min={0}
            setValue={
              invoiceBilling.inputs.perOrderSettings.inputs.includedOrders
                .setValue
            }
            step={1}
            value={
              invoiceBilling.inputs.perOrderSettings.inputs.includedOrders.value
            }
          />
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Price per order (USD)"
            min={0}
            prefix="$"
            setValue={
              invoiceBilling.inputs.perOrderSettings.inputs
                .overagePricePerOrderUsd.setValue
            }
            step={0.000_001}
            value={
              invoiceBilling.inputs.perOrderSettings.inputs
                .overagePricePerOrderUsd.value
            }
          />
        </Flex>
      )}

      <FormSwitch
        disabled={!isAuthorized || !v2Enabled}
        disabledTooltip={
          !v2Enabled
            ? "Order Tracking V2 must be enabled to bill per shipment"
            : undefined
        }
        input={invoiceBilling.inputs.perShipmentSettings.inputs.enabled}
        label="Bill Per Shipment (v2 only)"
      >
        Enable per-shipment billing (requires Order Tracking V2)
      </FormSwitch>
      {invoiceBilling.inputs.perShipmentSettings.inputs.enabled.value &&
        v2Enabled && (
          <Flex dir="column" gap="xl" pl="xl">
            <RedoIncrementDecrement
              disabled={!isAuthorized}
              fullWidth
              label="Included shipments per period"
              min={0}
              setValue={
                invoiceBilling.inputs.perShipmentSettings.inputs
                  .includedShipments.setValue
              }
              step={1}
              value={
                invoiceBilling.inputs.perShipmentSettings.inputs
                  .includedShipments.value
              }
            />
            <RedoIncrementDecrement
              disabled={!isAuthorized}
              fullWidth
              label="Overage price per shipment (USD)"
              min={0}
              prefix="$"
              setValue={
                invoiceBilling.inputs.perShipmentSettings.inputs
                  .overagePricePerShipmentUsd.setValue
              }
              step={0.000_001}
              value={
                invoiceBilling.inputs.perShipmentSettings.inputs
                  .overagePricePerShipmentUsd.value
              }
            />
          </Flex>
        )}

      <FormSwitch
        disabled={!isAuthorized}
        input={invoiceBilling.inputs.perSmsSettings.inputs.enabled}
        label="Bill Per SMS"
      >
        Enable SMS/MMS billing
      </FormSwitch>
      {invoiceBilling.inputs.perSmsSettings.inputs.enabled.value && (
        <Flex dir="column" gap="xl" pl="xl">
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Included SMS credits per month"
            min={0}
            setValue={
              invoiceBilling.inputs.perSmsSettings.inputs.includedCredits
                .setValue
            }
            step={1}
            value={
              invoiceBilling.inputs.perSmsSettings.inputs.includedCredits.value
            }
          />
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Overage price per SMS (USD)"
            min={0}
            prefix="$"
            setValue={
              invoiceBilling.inputs.perSmsSettings.inputs.overagePricePerSmsUsd
                .setValue
            }
            step={0.000_001}
            value={
              invoiceBilling.inputs.perSmsSettings.inputs.overagePricePerSmsUsd
                .value
            }
          />
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Overage price per MMS (USD)"
            min={0}
            prefix="$"
            setValue={
              invoiceBilling.inputs.perSmsSettings.inputs.overagePricePerMmsUsd
                .setValue
            }
            step={0.000_001}
            value={
              invoiceBilling.inputs.perSmsSettings.inputs.overagePricePerMmsUsd
                .value
            }
          />
          <FormSwitch
            disabled={!isAuthorized}
            input={
              invoiceBilling.inputs.perSmsSettings.inputs.chargeCarrierFees
            }
            label="Charge for carrier fees"
          >
            Charge carrier fees
          </FormSwitch>
        </Flex>
      )}

      <FormSwitch
        disabled={!isAuthorized}
        input={invoiceBilling.inputs.perEmailSettings.inputs.enabled}
        label="Bill Per Email"
      >
        Enable email billing
      </FormSwitch>
      {invoiceBilling.inputs.perEmailSettings.inputs.enabled.value && (
        <Flex dir="column" gap="xl" pl="xl">
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Included emails per month"
            min={0}
            setValue={
              invoiceBilling.inputs.perEmailSettings.inputs.includedEmails
                .setValue
            }
            step={1}
            value={
              invoiceBilling.inputs.perEmailSettings.inputs.includedEmails.value
            }
          />
          <RedoIncrementDecrement
            disabled={!isAuthorized}
            fullWidth
            label="Overage price per email (USD)"
            min={0}
            prefix="$"
            setValue={
              invoiceBilling.inputs.perEmailSettings.inputs
                .overagePricePerEmailUsd.setValue
            }
            step={0.000_001}
            value={
              invoiceBilling.inputs.perEmailSettings.inputs
                .overagePricePerEmailUsd.value
            }
          />
        </Flex>
      )}
    </>
  );

  return (
    <Card title="Order Tracking Billing">
      <section className={(gridCss.grid, gridCss.gap1)}>
        <Flex className={gridCss.span6L} dir="column" gap="xl">
          {activeSubscription && isEnabled && (
            <div>
              <h3>Active Subscription</h3>
              <p>Name: {activeSubscription?.name}</p>
              <p>Status: {activeSubscription?.status}</p>
            </div>
          )}
          <FormSwitch disabled={!isAuthorized} input={enabled} label="Enabled">
            Enabling billing will prompt the merchant to accept a usage-based
            subscription based on the settings below. Disabling billing will not
            affect the current subscription. Canceling the subscription is
            possible from the merchant dashboard "Billing" page
          </FormSwitch>
          <FormSwitch
            disabled={!isAuthorized}
            input={rerouteBillingToMarketing}
            label="Reroute billing to marketing"
          />
          {enabled?.value && !rerouteBillingToMarketing.value && (
            <>
              <FormSwitch
                disabled={!isAuthorized || shouldDisableToggle}
                input={invoiceBilling.inputs.enabled}
                label="Enable Invoice Billing"
              />
              {invoiceBilling.inputs.enabled.value ? (
                <>
                  <Flex
                    dir="column"
                    gap="xl"
                    style={{
                      border: legacySettingsAreActive
                        ? "2px solid var(--redo-warning-color-dark)"
                        : "2px solid var(--redo-success-secondary-color)",
                      borderRadius: "8px",
                      padding: "16px",
                      backgroundColor: legacySettingsAreActive
                        ? "var(--redo-warning-color)"
                        : "var(--redo-surface-success-color)",
                    }}
                  >
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      textColor="primary"
                    >
                      {legacySettingsAreActive
                        ? "Invoice Billing Settings (Scheduled)"
                        : "Invoice Billing Settings (CURRENTLY ACTIVE)"}
                    </Text>
                    <Text fontSize="xs" textColor="secondary">
                      {legacySettingsAreActive
                        ? "These settings will become active when the start date arrives or is set to today or the past."
                        : "These settings are CURRENTLY ACTIVE. Invoice billing is in effect."}
                    </Text>
                    {invoiceBillingSettings}
                  </Flex>
                  <Flex
                    dir="column"
                    gap="xl"
                    style={{
                      border: legacySettingsAreActive
                        ? "2px solid var(--redo-success-secondary-color)"
                        : "2px solid var(--redo-warning-color-dark)",
                      borderRadius: "8px",
                      padding: "16px",
                      backgroundColor: legacySettingsAreActive
                        ? "var(--redo-surface-success-color)"
                        : "var(--redo-warning-color)",
                      marginTop: "16px",
                    }}
                  >
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      textColor="primary"
                    >
                      {legacySettingsAreActive
                        ? "Legacy Billing Settings (CURRENTLY ACTIVE)"
                        : "Legacy Billing Settings (Inactive)"}
                    </Text>
                    <Text fontSize="xs" textColor="secondary">
                      {legacySettingsAreActive
                        ? "These settings are CURRENTLY ACTIVE because the Invoice Billing start date is in the future or not set. Once the start date arrives, Invoice Billing will take over and these settings will no longer apply."
                        : "These settings are not applied while Invoice Billing is active. They will only be applied if the Invoice Billing start date is moved to the future, or if Invoice Billing is turned off."}
                    </Text>
                    {legacyBillingSettings}
                  </Flex>
                </>
              ) : (
                legacyBillingSettings
              )}
            </>
          )}
        </Flex>
      </section>
    </Card>
  );
});
